import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { ChevronDown, Menu, X } from "lucide-react";
import { FEATURES as FLAGS } from "@markfeed/shared";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { cn } from "@/lib/utils";

const FEATURES = [
  { to: "/features/website-feedback", label: "Website feedback", hint: "Click-to-comment on any live site" },
  { to: "/features/client-feedback", label: "Client reviews & approvals", hint: "No-login review links" },
  { to: "/features/task-management", label: "Task management", hint: "Kanban for every revision" },
  ...(FLAGS.mockups ? [{ to: "/features/mockup-feedback", label: "Mockup feedback", hint: "Comment on designs & PDFs" }] : []),
];
const SOLUTIONS = [
  { to: "/for/agencies", label: "For agencies" },
  { to: "/for/freelancers", label: "For freelancers" },
  { to: "/for/startups", label: "For startups & product teams" },
];

function Dropdown({ label, items }: { label: string; items: { to: string; label: string; hint?: string }[] }) {
  return (
    <div className="group relative">
      <button className="flex items-center gap-1 rounded-md px-3 py-2 text-[14px] font-medium text-slate-600 hover:text-ink cursor-pointer" aria-haspopup="true">
        {label} <ChevronDown className="size-3.5 transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
      </button>
      <div className="invisible absolute left-0 top-full z-40 pt-2 opacity-0 transition-all group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <ul className="w-72 rounded-xl border border-line bg-white p-2 shadow-pop">
          {items.map((i) => (
            <li key={i.to}>
              <Link to={i.to} className="block rounded-lg px-3 py-2 hover:bg-slate-50">
                <span className="block text-sm font-medium text-ink">{i.label}</span>
                {i.hint && <span className="block text-xs text-slate-500">{i.hint}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function MarketingHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className={cn("sticky top-0 z-40 border-b transition-colors", scrolled ? "border-line bg-white/90 backdrop-blur" : "border-transparent bg-canvas")}>
      <a href="#content" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:rounded focus:bg-[var(--surface)] focus:px-3 focus:py-1">
        Skip to content
      </a>
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-5">
        <Link to="/" aria-label="MarkFeed home">
          <Logo />
        </Link>
        <nav aria-label="Primary" className="ml-6 hidden items-center lg:flex">
          <Dropdown label="Product" items={FEATURES} />
          <Dropdown label="Solutions" items={SOLUTIONS} />
          <NavLink to="/pricing" className="rounded-md px-3 py-2 text-[14px] font-medium text-slate-600 hover:text-ink">
            Pricing
          </NavLink>
          <NavLink to="/blog" className="rounded-md px-3 py-2 text-[14px] font-medium text-slate-600 hover:text-ink">
            Blog
          </NavLink>
        </nav>
        <div className="ml-auto hidden items-center gap-2 lg:flex">
          <ThemeToggle />
          <Button variant="ghost" asChild>
            <Link to="/login">Log in</Link>
          </Button>
          <Button asChild>
            <Link to="/signup">Start Free</Link>
          </Button>
        </div>
        <ThemeToggle className="ml-auto lg:hidden" />
        <button className="rounded-md p-2 lg:hidden" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>
      {open && (
        <nav aria-label="Mobile" className="border-t border-line bg-white px-5 pb-6 pt-3 lg:hidden">
          <p className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Product</p>
          {FEATURES.map((f) => (
            <Link key={f.to} to={f.to} className="block rounded-md px-2 py-2 text-[15px] font-medium">
              {f.label}
            </Link>
          ))}
          <p className="px-2 pb-1 pt-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Solutions</p>
          {SOLUTIONS.map((f) => (
            <Link key={f.to} to={f.to} className="block rounded-md px-2 py-2 text-[15px] font-medium">
              {f.label}
            </Link>
          ))}
          <Link to="/pricing" className="block rounded-md px-2 py-2 text-[15px] font-medium">
            Pricing
          </Link>
          <Link to="/blog" className="block rounded-md px-2 py-2 text-[15px] font-medium">
            Blog
          </Link>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button variant="outline" asChild>
              <Link to="/login">Log in</Link>
            </Button>
            <Button asChild>
              <Link to="/signup">Start Free</Link>
            </Button>
          </div>
        </nav>
      )}
    </header>
  );
}

export function MarketingFooter() {
  const cols: { title: string; links: [string, string][] }[] = [
    { title: "Product", links: [["Website feedback", "/features/website-feedback"], ["Client approvals", "/features/client-feedback"], ["Task management", "/features/task-management"], ...(FLAGS.mockups ? ([["Mockup feedback", "/features/mockup-feedback"]] as [string, string][]) : []), ["Pricing", "/pricing"]] },
    { title: "Solutions", links: [["Agencies", "/for/agencies"], ["Freelancers", "/for/freelancers"], ["Startups", "/for/startups"], ["All features", "/features"]] },
    { title: "Company", links: [["About", "/about"], ["Blog", "/blog"], ["Contact", "/contact"], ["See the demo", "/review/demoabcfurniture2026review"]] },
  ];
  return (
    <footer className="border-t border-line bg-white">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Logo />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
            Visual website feedback for Indian agencies, freelancers and product teams. Clients click, you fix, everyone approves.
          </p>
          <p className="mt-4 text-sm text-slate-500">Made in India 🇮🇳 · Prices in ₹ INR with GST invoices</p>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <h2 className="text-sm font-semibold text-ink">{c.title}</h2>
            <ul className="mt-3 space-y-2">
              {c.links.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-slate-500 hover:text-ink">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-line">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-5 py-5 text-xs text-slate-500 sm:flex-row sm:justify-between">
          <span>© {new Date().getFullYear()} MarkFeed. All rights reserved.</span>
          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-1">
            {([
              ["Contact", "/contact"],
              ["Privacy Policy", "/privacy"],
              ["Refund & Return Policy", "/refund-policy"],
              ["Terms & Conditions", "/terms"],
            ] as const).map(([label, to]) => (
              <Link key={to} to={to} className="hover:text-ink">
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}

export function MarketingLayout() {
  const loc = useLocation();
  useEffect(() => {
    if (!loc.hash) window.scrollTo(0, 0);
  }, [loc.pathname, loc.hash]);
  return (
    <div className="min-h-screen bg-canvas">
      <MarketingHeader />
      <main id="content">
        <Outlet />
      </main>
      <MarketingFooter />
    </div>
  );
}

/** `page` makes the title the page's <h1> (use it when the Section is the top of a page). */
export function Section({ id, eyebrow, title, intro, children, className, page }: { id?: string; eyebrow?: string; title: React.ReactNode; intro?: React.ReactNode; children?: React.ReactNode; className?: string; page?: boolean }) {
  const Heading = page ? "h1" : "h2";
  return (
    <section id={id} className={cn("mx-auto max-w-6xl px-5 py-20 sm:py-24", className)}>
      <div className="max-w-2xl">
        {eyebrow && <p className="text-[13px] font-semibold uppercase tracking-wider text-brand-600">{eyebrow}</p>}
        <Heading className="mt-2 text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{title}</Heading>
        {intro && <p className="mt-4 text-[17px] leading-relaxed text-slate-600">{intro}</p>}
      </div>
      {children && <div className="mt-12">{children}</div>}
    </section>
  );
}
